import { Link } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import {
  Shield, Lock, Eye, Gauge, ServerCog, AlertTriangle,
  CheckCircle2, FileCheck2, Mail, ArrowRight,
} from "lucide-react";

/**
 * Public Security Posture page (/security).
 *
 * Plain-English transparency about how Newseze protects the LLM scoring
 * surface, reader data, and the brand. Designed for three audiences at once:
 * - Readers ("can I trust this?")
 * - Publishers + advertisers ("is this safe for our ad spend / AdSense?")
 * - Enterprise buyers ("does this clear our compliance review?")
 */
export default function Security() {
  return (
    <div className="min-h-screen bg-[var(--bg)]">
      <Header />
      <main className="max-w-[920px] mx-auto px-5 py-10" data-testid="security-page">

        {/* Hero */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 text-[10.5px] uppercase tracking-[0.22em] font-black text-[var(--gold)] mb-3">
            <Shield size={13} /> Newseze Security Posture
          </div>
          <h1 className="text-[32px] sm:text-[44px] lg:text-[54px] font-black tracking-[-0.025em] leading-[1.04] mb-3" data-testid="security-h1">
            The only news platform that treats<br className="hidden sm:block" /> AI safety as a feature.
          </h1>
          <p className="text-[14px] sm:text-[16px] text-[var(--muted)] max-w-[680px] mx-auto leading-snug">
            No drama, no conspiracies, and no AI shortcuts that could compromise your reading
            experience or our advertisers. Here&apos;s how Newseze protects every paste, every
            score, and every Questeze answer.
          </p>
        </div>

        {/* 5 layers */}
        <section className="mb-12">
          <div className="text-[11px] uppercase tracking-[0.18em] font-black text-[var(--blue)] mb-3 border-l-4 border-[var(--gold)] pl-3">
            Five defense layers · every LLM call
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="security-layers">
            <Layer
              n="01"
              icon={Lock}
              title="Input firewall"
              testid="layer-firewall"
              body="Every piece of content you paste — URL, screenshot, post, article — is wrapped in tagged delimiters before reaching the AI. The model is instructed to SCORE what's inside, never to FOLLOW it as instructions."
            />
            <Layer
              n="02"
              icon={AlertTriangle}
              title="Prompt-injection detector"
              testid="layer-injection"
              body="Fourteen pattern checks catch jailbreak attempts (&quot;ignore previous instructions&quot;, &quot;reveal your system prompt&quot;, &quot;you are now DAN&quot;) BEFORE we ever call the AI. Attempts get a synthetic max-drama score — costs us nothing, alerts you instantly."
            />
            <Layer
              n="03"
              icon={Eye}
              title="Output scrub"
              testid="layer-scrub"
              body="Every AI response is regex-stripped of any phrase that would reveal our editorial recipe. Defense in depth — if the model ever leaks, the client never sees it."
            />
            <Layer
              n="04"
              icon={Gauge}
              title="Per-identity rate limits"
              testid="layer-burst"
              body="Each reader is capped at 8 AI calls per minute, 60 per hour. Prevents abuse, VPN-rotation gaming, and protects the budget for legitimate readers and our paying subscribers."
            />
            <Layer
              n="05"
              icon={ServerCog}
              title="Global daily budget gate"
              testid="layer-budget"
              body="A hard ceiling on total daily AI calls means no single bad actor — no viral spike, no scraping run — can drain our spend in a single day. Paying tiers are isolated and unaffected."
            />
            <Layer
              n="06"
              icon={FileCheck2}
              title="Content-quality scoring only"
              testid="layer-quality"
              body="When you score a social post or article, Newseze never personally attacks the author. We score the CLAIM, not the person. If a post itself is a personal attack, we refuse to amplify it."
              gold
            />
          </div>
        </section>

        {/* Brand-safety guarantees */}
        <section className="mb-12" data-testid="security-brand">
          <div className="text-[11px] uppercase tracking-[0.18em] font-black text-[var(--blue)] mb-3 border-l-4 border-[var(--gold)] pl-3">
            Brand-safety guarantees · for readers, advertisers, partners
          </div>
          <ul className="space-y-2.5">
            {[
              ["No drama amplification.", "Inputs that read as conspiracy, rage, or personal attacks are flagged and excluded from our public feed — not boosted for engagement."],
              ["No reader-data sale.", "Reader emails, custom feed preferences, and Questeze transcripts are never sold, never licensed to data brokers, and never used to train external AI models."],
              ["No drive-by malware.", "All outbound links to publishers carry rel='nofollow noopener noreferrer' + referrerpolicy='no-referrer'. Compliant with Google AdSense brand-safety standards."],
              ["No surprise charges.", "Subscription tiers ($6.99 Reader · $10.99 Pro · $19.99 Channels · $49.99 Elite) are exactly what you pay. Cancel anytime, no questions, no dark patterns."],
              ["DMCA + Publisher Opt-Out.", "Right-holders can take down any quoted material in one form submission. Publishers can block Newseze entirely with a single email."],
            ].map(([title, body], i) => (
              <li key={i} className="flex items-start gap-3 bg-white rounded-md border border-[var(--line)] p-3.5">
                <CheckCircle2 size={16} className="text-emerald-600 shrink-0 mt-0.5" />
                <div>
                  <b className="text-[13.5px] block">{title}</b>
                  <span className="text-[12.5px] text-[var(--muted)]">{body}</span>
                </div>
              </li>
            ))}
          </ul>
        </section>

        {/* What to do if you see something */}
        <section className="rounded-xl border-2 border-[var(--gold)] bg-white p-5 sm:p-6 mb-10" data-testid="security-report">
          <h3 className="text-[20px] font-black tracking-[-0.015em] mb-2 flex items-center gap-2">
            <AlertTriangle size={18} className="text-[var(--gold)]" />
            Found something concerning?
          </h3>
          <p className="text-[13.5px] text-[var(--muted)] mb-4 leading-snug">
            If you discover what looks like a security issue, a jailbroken score, a leaked
            system instruction, or any content that violates our calm-news pillars, please
            tell us. We treat every report seriously and respond within 48 hours.
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <a
              href="mailto:security@newseze.com"
              className="btn primary"
              data-testid="security-email"
            >
              <Mail size={13} /> security@newseze.com
            </a>
            <Link to="/legal" className="btn" data-testid="security-legal-link">
              DMCA & Legal <ArrowRight size={12} />
            </Link>
          </div>
        </section>

        {/* Footer note */}
        <p className="text-[11px] text-[var(--muted)] italic text-center pb-4">
          Newseze security posture last updated June 12, 2026. Architecture details available
          on request for enterprise customers and journalists covering AI safety.
        </p>

      </main>
      <Footer />
    </div>
  );
}

function Layer({ n, icon: Icon, title, body, testid, gold = false }) {
  return (
    <div
      className={`rounded-xl p-4 sm:p-5 border transition ${
        gold
          ? "bg-[var(--gold)] text-[#1d1303] border-[var(--gold)]"
          : "bg-white border-[var(--line)] hover:border-[var(--gold)]"
      }`}
      data-testid={testid}
    >
      <div className="flex items-center gap-2 mb-1.5">
        <span className={`text-[28px] font-black tabular-nums leading-none ${gold ? "text-[#7a5b00]" : "text-[var(--gold)]"}`}>{n}</span>
        <Icon size={16} className={gold ? "text-[#1d1303]" : "text-[var(--blue)]"} />
      </div>
      <h4 className={`text-[15px] font-black tracking-[-0.01em] mb-1 ${gold ? "text-[#1d1303]" : "text-[var(--ink)]"}`}>{title}</h4>
      <p
        className={`text-[12.5px] leading-snug ${gold ? "text-[#3d2a00]" : "text-[var(--muted)]"}`}
        dangerouslySetInnerHTML={{ __html: body }}
      />
    </div>
  );
}
